import { useState } from 'react';
import { api, getErrorMessage } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertTriangle, Trash2, CheckCircle2, Clock, Shield, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';

export default function DeleteEntityDialog({ 
  open, 
  onOpenChange, 
  entityType,  // 'hotel' or 'company'
  entityId,
  entityName,
  onDeleted 
}) {
  const [step, setStep] = useState('confirm'); // 'confirm', 'otp', 'executing'
  const [deletionRequestId, setDeletionRequestId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [otpData, setOtpData] = useState({
    companyAdminVerified: false,
    superAdminVerified: false,
    companyAdminOtp: '',
    superAdminOtp: '',
    companyAdminEmail: '',
    superAdminEmail: '',
    // Demo mode OTP hints
    companyAdminOtpHint: '',
    superAdminOtpHint: '',
    requiresCompanyAdmin: true
  });

  const handleInitiateDelete = async () => {
    setLoading(true);
    try {
      const response = await api.post('/deletion/initiate', {
        entity_type: entityType,
        entity_id: entityId
      });
      
      setDeletionRequestId(response.data.deletion_request_id);
      const requiresCompanyAdmin = response.data.requires_company_admin !== false;
      
      setOtpData(prev => ({
        ...prev,
        companyAdminEmail: response.data.company_admin_email,
        superAdminEmail: response.data.super_admin_email,
        companyAdminOtpHint: response.data.company_admin_otp_hint || '',
        superAdminOtpHint: response.data.super_admin_otp_hint || '',
        requiresCompanyAdmin: requiresCompanyAdmin,
        companyAdminVerified: !requiresCompanyAdmin  // Auto-verified if no company admin
      }));
      setStep('otp');
      toast.success(requiresCompanyAdmin ? 'OTPs sent to both admins' : 'OTP sent to Super Admin');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to initiate deletion'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (adminType) => {
    const otp = adminType === 'company_admin' ? otpData.companyAdminOtp : otpData.superAdminOtp;
    
    if (!otp || otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post('/deletion/verify-otp', {
        deletion_request_id: deletionRequestId,
        admin_type: adminType,
        otp: otp
      });
      
      setOtpData(prev => ({
        ...prev,
        companyAdminVerified: response.data.company_admin_verified,
        superAdminVerified: response.data.super_admin_verified
      }));
      
      toast.success(`${adminType === 'company_admin' ? 'Company Admin' : 'Super Admin'} OTP verified`);
      
      if (response.data.ready_for_deletion) {
        toast.success('Both admins verified! Ready to execute deletion.');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Invalid OTP'));
    } finally {
      setLoading(false);
    }
  };

  const handleExecuteDeletion = async () => {
    setStep('executing');
    setLoading(true);
    try {
      const response = await api.post(`/deletion/execute/${deletionRequestId}`);
      toast.success(response.data.message);
      onDeleted && onDeleted();
      handleClose();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to execute deletion'));
      setStep('otp');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (deletionRequestId) {
      try {
        await api.post(`/deletion/cancel/${deletionRequestId}`);
        toast.info('Deletion request cancelled');
      } catch (error) {
        console.error('Failed to cancel deletion request');
      }
    }
    handleClose();
  };

  const handleClose = () => {
    setStep('confirm');
    setDeletionRequestId(null);
    setOtpData({
      companyAdminVerified: false,
      superAdminVerified: false,
      companyAdminOtp: '',
      superAdminOtp: '',
      companyAdminEmail: '',
      superAdminEmail: '',
      companyAdminOtpHint: '',
      superAdminOtpHint: '',
      requiresCompanyAdmin: true
    });
    onOpenChange(false);
  };

  const bothVerified = otpData.companyAdminVerified && otpData.superAdminVerified;

  return (
    <Dialog open={open} onOpenChange={(open) => !open && handleCancel()}>
      <DialogContent className="max-w-lg bg-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="h-5 w-5" />
            Delete {entityType === 'hotel' ? 'Hotel' : 'Company'}
          </DialogTitle>
          <DialogDescription>
            {step === 'confirm' && (
              <>This action will permanently delete <strong>{entityName}</strong> and all associated data.</>
            )}
            {step === 'otp' && (
              <>Verify OTPs from both admins to proceed with deletion.</>
            )}
            {step === 'executing' && (
              <>Deleting all data. Please wait...</>
            )}
          </DialogDescription>
        </DialogHeader>

        {step === 'confirm' && (
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h4 className="font-semibold text-red-800 mb-2">⚠️ Warning: Irreversible Action</h4>
              <p className="text-sm text-red-700 mb-3">
                Deleting "{entityName}" will permanently remove:
              </p>
              <ul className="text-sm text-red-700 list-disc list-inside space-y-1">
                {entityType === 'hotel' ? (
                  <>
                    <li>All rooms and room types</li>
                    <li>All bookings and guest records</li>
                    <li>All invoices and payments</li>
                    <li>All restaurant data and orders</li>
                    <li>All settings and configurations</li>
                  </>
                ) : (
                  <>
                    <li>All hotels under this company</li>
                    <li>All rooms, bookings, and guest data</li>
                    <li>All users associated with this company</li>
                    <li>All financial and operational data</li>
                  </>
                )}
              </ul>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-800 flex items-center gap-2">
                <Shield className="h-4 w-4" />
                Dual OTP Verification Required
              </h4>
              <p className="text-sm text-blue-700 mt-1">
                Both Company Admin and Super Admin must verify via OTP before deletion can proceed.
              </p>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>Cancel</Button>
              <Button 
                variant="destructive" 
                onClick={handleInitiateDelete}
                disabled={loading}
              >
                {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <AlertTriangle className="h-4 w-4 mr-2" />}
                Proceed to Verification
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 'otp' && (
          <div className="space-y-4">
            {/* Company Admin OTP - Only show if requires company admin */}
            {otpData.requiresCompanyAdmin && (
              <Card className={otpData.companyAdminVerified ? 'border-green-300 bg-green-50' : ''}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center justify-between">
                    <span>Company Admin Verification</span>
                    {otpData.companyAdminVerified ? (
                      <Badge className="bg-green-500"><CheckCircle2 className="h-3 w-3 mr-1" /> Verified</Badge>
                    ) : (
                      <Badge variant="outline"><Clock className="h-3 w-3 mr-1" /> Pending</Badge>
                    )}
                  </CardTitle>
                  <CardDescription className="text-xs">
                    OTP sent to: {otpData.companyAdminEmail}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {!otpData.companyAdminVerified && (
                    <div className="flex gap-2">
                      <Input
                        type="text"
                        maxLength={6}
                        placeholder="Enter 6-digit OTP"
                        value={otpData.companyAdminOtp}
                        onChange={(e) => setOtpData(prev => ({ ...prev, companyAdminOtp: e.target.value.replace(/\D/g, '') }))}
                        className="flex-1"
                      />
                      <Button 
                        onClick={() => handleVerifyOtp('company_admin')}
                        disabled={loading || otpData.companyAdminOtp.length !== 6}
                        size="sm"
                      >
                        Verify
                      </Button>
                    </div>
                  )}
                  {otpData.companyAdminOtpHint && !otpData.companyAdminVerified && (
                    <p className="text-xs text-orange-600 mt-2">
                      Demo Mode - OTP: <code className="bg-orange-100 px-1 rounded">{otpData.companyAdminOtpHint}</code>
                    </p>
                  )}
                </CardContent>
              </Card>
            )}
            
            {/* Notice if no company admin */}
            {!otpData.requiresCompanyAdmin && (
              <Card className="border-blue-300 bg-blue-50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2 text-blue-700">
                    <Shield className="h-4 w-4" />
                    Single Admin Verification
                  </CardTitle>
                  <CardDescription className="text-xs text-blue-600">
                    No Company Admin found. Only Super Admin verification is required.
                  </CardDescription>
                </CardHeader>
              </Card>
            )}

            {/* Super Admin OTP */}
            <Card className={otpData.superAdminVerified ? 'border-green-300 bg-green-50' : ''}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center justify-between">
                  <span>Super Admin Verification</span>
                  {otpData.superAdminVerified ? (
                    <Badge className="bg-green-500"><CheckCircle2 className="h-3 w-3 mr-1" /> Verified</Badge>
                  ) : (
                    <Badge variant="outline"><Clock className="h-3 w-3 mr-1" /> Pending</Badge>
                  )}
                </CardTitle>
                <CardDescription className="text-xs">
                  OTP sent to: {otpData.superAdminEmail}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!otpData.superAdminVerified && (
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      maxLength={6}
                      placeholder="Enter 6-digit OTP"
                      value={otpData.superAdminOtp}
                      onChange={(e) => setOtpData(prev => ({ ...prev, superAdminOtp: e.target.value.replace(/\D/g, '') }))}
                      className="flex-1"
                    />
                    <Button 
                      onClick={() => handleVerifyOtp('super_admin')}
                      disabled={loading || otpData.superAdminOtp.length !== 6}
                      size="sm"
                    >
                      Verify
                    </Button>
                  </div>
                )}
                {otpData.superAdminOtpHint && !otpData.superAdminVerified && (
                  <p className="text-xs text-orange-600 mt-2">
                    Demo Mode - OTP: <code className="bg-orange-100 px-1 rounded">{otpData.superAdminOtpHint}</code>
                  </p>
                )}
              </CardContent>
            </Card>

            <DialogFooter>
              <Button variant="outline" onClick={handleCancel}>Cancel Deletion</Button>
              <Button 
                variant="destructive" 
                onClick={handleExecuteDeletion}
                disabled={!bothVerified || loading}
              >
                {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                Execute Permanent Deletion
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 'executing' && (
          <div className="py-8 text-center">
            <Loader2 className="h-12 w-12 animate-spin mx-auto text-red-500 mb-4" />
            <p className="text-gray-600">Permanently deleting all data...</p>
            <p className="text-sm text-gray-400 mt-2">This may take a moment.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
